/**
 * Input tax credit, and what a return would say.
 *
 * The rule this screen exists to make visible: **2B never auto-claims**. The
 * government's statement is evidence about what suppliers filed, not a list of
 * credits to take. So a bill shows as *undecided* until a person decides, and
 * undecided is a state to clear rather than a quiet default.
 *
 * The return below is a working paper. This system prepares and reconciles; a
 * person files.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Loading, Note, Pill } from '../components/ui'

interface ItcRow {
  invoice_id: string
  reference: string
  party_name: string
  invoice_date: string
  cgst_paise: number
  sgst_paise: number
  igst_paise: number
  decision: string | null
  reason: string | null
  in_2b: boolean
  //: Recorded from a bank payment. Its credit waits for the supplier's
  //: invoice: attached, due or overdue.
  paid_from_bank: boolean
  invoice_state: string | null
  invoice_due_on: string | null
}

//: Why Claim is not offered yet, or null.
function heldForInvoice(row: ItcRow): string | null {
  if (!row.paid_from_bank || row.invoice_state === 'attached') return null
  return `Paid from the bank; the supplier's invoice ${
    row.invoice_state === 'overdue' ? 'was due' : 'is due'
  } by ${row.invoice_due_on}. Attach it on the Banking or Bills screen, then claim.`
}

interface Gstr3b {
  period_month: string
  gstin: string | null
  outward_taxable_paise: number
  outward_cgst_paise: number
  outward_sgst_paise: number
  outward_igst_paise: number
  rcm_taxable_paise: number
  rcm_cgst_paise: number
  rcm_sgst_paise: number
  rcm_igst_paise: number
  itc_cgst_paise: number
  itc_sgst_paise: number
  itc_igst_paise: number
  deferred_count: number
  deferred_paise: number
  undecided_count: number
  net_payable_paise: number
  notes: string[]
  refused: Record<string, string>
}

interface Mismatch {
  invoice_id: string
  reference: string
  kind: string
  detail: string
}

const thisMonth = () => `${new Date().toISOString().slice(0, 7)}-01`

export default function Gst() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [month, setMonth] = useState(thisMonth())
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['itc'] })
    void queryClient.invalidateQueries({ queryKey: ['gstr3b'] })
    void queryClient.invalidateQueries({ queryKey: ['2b-differences'] })
  }

  const itc = useQuery({
    queryKey: ['itc'],
    queryFn: () => api.get<ItcRow[]>('/tax/gst/itc'),
  })
  const summary = useQuery({
    queryKey: ['gstr3b', month],
    queryFn: () => api.get<Gstr3b>(`/tax/gst/3b?period_month=${month}`),
  })
  const differences = useQuery({
    queryKey: ['2b-differences', month],
    queryFn: () => api.get<Mismatch[]>(`/tax/gst/2b-differences?period_month=${month}`),
  })

  const decide = useMutation({
    mutationFn: ({
      id,
      decision,
      reason,
    }: {
      id: string
      decision: string
      reason?: string
    }) => api.post<ItcRow>(`/tax/gst/itc/${id}`, { decision, reason: reason ?? null }),
    onSuccess: refresh,
  })
  const mark2b = useMutation({
    mutationFn: (row: ItcRow) =>
      api.post<ItcRow>(`/tax/gst/2b/${row.invoice_id}`, {
        cgst_paise: row.cgst_paise,
        sgst_paise: row.sgst_paise,
        igst_paise: row.igst_paise,
      }),
    onSuccess: refresh,
  })

  if (itc.isLoading) return <Loading what="Loading input tax credit" />
  const rows = itc.data ?? []
  const undecided = rows.filter((r) => r.decision === null)

  const askAndDefer = (id: string) => {
    const reason = window.prompt(
      'Why is this credit not being taken? It is the question the return preparer has to answer, and next March nobody will remember.',
    )
    if (reason && reason.trim()) decide.mutate({ id, decision: 'deferred', reason })
  }

  return (
    <>
      {/* The return and the 2B differences too, not only the bill list. A
          return that failed to load used to leave its card empty with nothing
          said -- which reads exactly like a month with no figures. */}
      <ErrorNote
        error={
          itc.error ?? summary.error ?? differences.error ?? decide.error ?? mark2b.error
        }
      />

      {undecided.length > 0 && (
        <Note kind="warn">
          {undecided.length} {undecided.length === 1 ? 'bill carries' : 'bills carry'} tax
          that nobody has decided about. They are not claimed by default — an undecided
          credit is missing from the return until somebody says to take it.
        </Note>
      )}

      <Card
        title="What a return would say"
        hint={month.slice(0, 7)}
        actions={
          <input
            type="month"
            value={month.slice(0, 7)}
            onChange={(e) => setMonth(`${e.target.value}-01`)}
          />
        }
      >
        {summary.isLoading && <Loading what="Working out the return" />}
        {summary.data && (
          <>
            <div className="row">
              <div>
                <div className="caption">Output tax</div>
                <div className="num">
                  {formatInr(
                    summary.data.outward_cgst_paise +
                      summary.data.outward_sgst_paise +
                      summary.data.outward_igst_paise,
                  )}
                </div>
              </div>
              <div>
                <div className="caption">Reverse charge</div>
                <div className="num">
                  {formatInr(
                    summary.data.rcm_cgst_paise +
                      summary.data.rcm_sgst_paise +
                      summary.data.rcm_igst_paise,
                  )}
                </div>
              </div>
              <div>
                <div className="caption">Credit claimed</div>
                <div className="num">
                  {formatInr(
                    summary.data.itc_cgst_paise +
                      summary.data.itc_sgst_paise +
                      summary.data.itc_igst_paise,
                  )}
                </div>
              </div>
              <div>
                <div className="caption">Net payable</div>
                <div className="num">
                  <strong>{formatInr(summary.data.net_payable_paise)}</strong>
                </div>
              </div>
            </div>

            {summary.data.deferred_count > 0 && (
              <Note kind="info">
                {formatInr(summary.data.deferred_paise)} of credit is being held back
                across {summary.data.deferred_count}{' '}
                {summary.data.deferred_count === 1 ? 'bill' : 'bills'}. It is not in the
                figures above.
              </Note>
            )}
            {summary.data.notes.map((note, i) => (
              <Note key={i} kind="warn">
                {note}
              </Note>
            ))}
            <Note kind="info">
              A working paper, not a filing. This system prepares and reconciles; a person
              files. Not prepared here at all:{' '}
              {Object.keys(summary.data.refused).join(', ')}.
            </Note>
          </>
        )}
      </Card>

      {(differences.data ?? []).length > 0 && (
        <Card
          title="Where the books and the filings disagree"
          hint={`${differences.data!.length}`}
          tight
        >
          <table>
            <thead>
              <tr>
                <th>Bill</th>
                <th>What</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {differences.data!.map((m) => (
                <tr key={m.invoice_id}>
                  <td className="mono">{m.reference}</td>
                  <td>
                    <Pill value={m.kind} />
                  </td>
                  <td className="muted">{m.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card title="Input tax credit" hint={`${rows.length} bills carrying tax`} tight>
        {rows.length === 0 ? (
          <div className="empty">No posted bills carry tax yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Bill</th>
                <th>Vendor</th>
                <th>Date</th>
                <th className="num">Tax</th>
                <th>In 2B</th>
                <th>Invoice</th>
                <th>Decision</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.invoice_id}>
                  <td className="mono">{row.reference}</td>
                  <td>{row.party_name}</td>
                  <td className="muted date">{row.invoice_date}</td>
                  <td className="num">
                    {formatInr(row.cgst_paise + row.sgst_paise + row.igst_paise)}
                  </td>
                  <td>
                    {row.in_2b ? (
                      <span className="muted">filed</span>
                    ) : (
                      <span className="faint">not filed</span>
                    )}
                  </td>
                  <td>
                    {!row.paid_from_bank ? (
                      <span className="faint">—</span>
                    ) : row.invoice_state === 'attached' ? (
                      <span className="muted">attached</span>
                    ) : (
                      <span
                        className={`pill ${row.invoice_state === 'overdue' ? 'blocking' : 'advisory'}`}
                      >
                        {row.invoice_state === 'overdue' ? 'overdue' : `due ${row.invoice_due_on}`}
                      </span>
                    )}
                  </td>
                  <td>
                    {row.decision ? (
                      <>
                        <Pill value={row.decision} />
                        {row.reason && <div className="faint">{row.reason}</div>}
                      </>
                    ) : (
                      <span className="faint">undecided</span>
                    )}
                  </td>
                  <td className="num">
                    {can('tax.gst_return.prepare') && (
                      <>
                        {!row.in_2b && (
                          <>
                            <button
                              className="small"
                              onClick={() => mark2b.mutate(row)}
                            >
                              Seen in 2B
                            </button>{' '}
                          </>
                        )}
                        {row.decision !== 'claimed' && (
                          <>
                            <button
                              className="small primary"
                              disabled={decide.isPending || heldForInvoice(row) !== null}
                              title={heldForInvoice(row) ?? undefined}
                              onClick={() =>
                                decide.mutate({ id: row.invoice_id, decision: 'claimed' })
                              }
                            >
                              Claim
                            </button>{' '}
                          </>
                        )}
                        {row.decision !== 'deferred' && (
                          <button
                            className="small"
                            onClick={() => askAndDefer(row.invoice_id)}
                          >
                            Hold back
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Note kind="info">
          Marking a bill as seen in 2B records a fact and decides nothing. A credit is
          claimed because a person decided to claim it.
        </Note>
      </Card>
    </>
  )
}
